import _ from 'lodash'

import type { ContextoApp } from '@comun/appsConfigs'
import type { NuxtAppConfig } from 'nuxt/schema'

import { TrosettaAPI } from './rosetta'

export const creadorSchema = {
  '@type': 'Person',
  name: 'Camilo Núñez',
  url: ['https://github.com/camonunez']
}

export const schemaDeLaOrganizacion = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Boxmagic',
  alternateName: 'Boxmagic',
  url: 'https://www.boxmagicapp.com',
  logo: {
    '@type': 'ImageObject',
    name: 'Boxmagic Logo',
    url: 'https://www.boxmagicapp.com/logo.png'
  },
  foundingDate: '2015',
  founders: [
    creadorSchema
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      email: 'hola@boxmagicapp.com',
      name: 'Email',
      contactType: 'Email',
      areaServed: 'Global'
    }
  ]
}

type Pais = 'CL' | 'MX' | 'US' | 'BR'

export function generarSchemaDeApp(p: {
    nombre: string,
    descripcion?: string,
    url: string,
    image?: string,

    paises: Pais[]
    iosLink?: string,
    androidLink?: string

}) {
  const categoriaDeApp = 'Health & Fitness'
  const offers: {
        "@type": "Offer"
        priceCurrency: 'USD' | 'MXN' | 'CLP' | 'BRL'
        price: "0.00"
    }[] = []

  for (const pais of p.paises) {
    if (pais === 'CL') {
      offers.push({
        "@type": "Offer",
        priceCurrency: 'USD',
        price: '0.00'
      })
    } else if (pais === 'MX') {
      offers.push({
        "@type": "Offer",
        priceCurrency: 'MXN',
        price: '0.00'
      })
    } else if (pais === 'US') {
      offers.push({
        "@type": "Offer",
        priceCurrency: 'USD',
        price: '0.00'
      })
    } else if (pais === 'BR') {
      offers.push({
        "@type": "Offer",
        priceCurrency: 'BRL',
        price: '0.00'
      })
    }
  }

  const hasPart: {
        "@type": "WebApplication" | "MobileApplication"
        name: string
        url: string
        browserRequirements?: string
        operatingSystem?: string
        downloadUrl?: string
    }[] = [
      {
        "@type": "WebApplication",
        "name": p.nombre,
        "url": p.url,
        "browserRequirements": "Chrome, Firefox, Safari"
      }
    ]

  if (p.iosLink) {
    hasPart.push({
      "@type": "MobileApplication",
      "name": p.nombre,
      "url": p.url,
      "operatingSystem": "iOS",
      "downloadUrl": p.iosLink
    })
  }

  if (p.androidLink) {
    hasPart.push({
      "@type": "MobileApplication",
      "name": p.nombre,
      "url": p.url,
      "operatingSystem": "Android, iOS",
      "downloadUrl": p.androidLink
    })
  }

  const result: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": p.nombre,
    "description": p.descripcion,
    "url": p.url,
    "operatingSystem": "Android, iOS, Web",
    "applicationCategory": categoriaDeApp,

    accountablePerson: _.pick(schemaDeLaOrganizacion, ['@context', '@type', 'name']),
    creator: creadorSchema,

    offers,
    hasPart
  }

  if (p.image) {
    result.image = p.image
  }

  return result
}

export function armarHead(contextoApp: ContextoApp, ruta?: string): NuxtAppConfig['head'] {

  const appConfig = contextoApp.appConfig
  const gTagManager = appConfig.gTagManager
  const scripts: NuxtAppConfig['head']['script'] = gTagManager
    ? [{
      id: 'gtm',
      async: true,
      src: `https://www.googletagmanager.com/gtm.js?id=${gTagManager}`,
      tagPosition: 'bodyClose'
    }, {
      id: 'gtmJS',
      tagPosition: 'bodyClose',
      innerHTML:
                'window.dataLayer = window.dataLayer || [];window.dataLayer.push({ \'gtm.start\': new Date().getTime(), event: \'gtm.js\' })'
    }]
    : []
  const url = appConfig.hosts[0]
  if (!url) throw new Error('No se encontró la URL de la aplicación')

  const host = appConfig.hosts[0] || ''
  const ogImage = appConfig.imagenOG || `https://${host}/og-image.png`
  const ogImageAncho = appConfig.imagenOGAncho || 1200
  const ogImageAlto = appConfig.imagenOGAlto || 500
  const themeColor = appConfig.themeColor || '#000000'
  const iconoApple = appConfig.iconoApple || '/apple-touch-icon.png'
  const rutaLimpia = ruta ? ruta.replace(/^\/+/, '').replace(/\/$/, '') : ''
  const canonicalUrl = rutaLimpia ? `https://${host}/${rutaLimpia}` : `https://${host}/`

  const schemaDeApp = generarSchemaDeApp({
    nombre: appConfig.nombre,
    descripcion: appConfig.descripcion,
    url,
    image: ogImage,
    paises: ['CL', 'MX', 'BR', 'US'],
    iosLink: appConfig.iosLink,
    androidLink: appConfig.androidLink
  })

  scripts.push({ id: 'schemaOrganizacion', type: 'application/ld+json', innerHTML: JSON.stringify(schemaDeLaOrganizacion) })
  scripts.push({
    id: 'schemaSitio',
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schemaDeApp)
  })

  const head: NuxtAppConfig['head'] = {
    title: appConfig.nombre,
    htmlAttrs: {
      lang: TrosettaAPI.idiomaVisible.value
    },
    meta: [
      { charset: 'utf-8' },
      {
        id: 'viewport',
        name: 'viewport',
        content:
                    'width=device-width, user-scalable=no, viewport-fit=cover, shrink-to-fit=no'
      },
      { id: 'description', name: 'description', content: appConfig.descripcion },
      // ANDROID
      {
        id: 'mobile-web-app-capable',
        name: 'mobile-web-app-capable',
        content: 'yes'
      },
      // IOS
      {
        id: 'apple-mobile-web-app-title',
        name: 'apple-mobile-web-app-title',
        content: appConfig.nombre
      },
      {
        id: 'mobile-web-app-capable',
        name: 'mobile-web-app-capable',
        content: 'yes'
      },
      {
        id: 'apple-mobile-web-app-status-bar-style',
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'default'
      },
      // OTROS
      {
        id: 'msapplication-tap-highlight',
        name: 'msapplication-tap-highlight',
        content: ''
      },
      // Desactivar darkreader
      {
        id: 'darkreader',
        name: 'darkreader-lock',
        content: 'darkreader-lock'
      },
      // Desactivar traductor
      {
        id: 'google',
        name: 'google',
        content: 'notranslate'
      },
      // OPEN GRAPH
      { property: 'og:title', content: appConfig.nombre },
      { property: 'og:description', content: appConfig.descripcion },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:width', content: String(ogImageAncho) },
      { property: 'og:image:height', content: String(ogImageAlto) },
      { property: 'og:image:alt', content: `${appConfig.nombre} - ${appConfig.descripcion}` },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: appConfig.nombre },
      { property: 'og:locale', content: 'es_CL' },
      { property: 'og:image:type', content: 'image/png' },
      // TWITTER CARDS
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:domain', content: host },
      { name: 'twitter:title', content: appConfig.nombre },
      { name: 'twitter:description', content: appConfig.descripcion },
      { name: 'twitter:image', content: ogImage },
      { name: 'twitter:image:alt', content: `${appConfig.nombre} - ${appConfig.descripcion}` },
      // THEME
      { name: 'theme-color', content: themeColor }
    ],
    link: [
      {
        id: 'favicon',
        rel: 'icon',
        type: 'image/svg+xml',
        href: '/favicon.svg'
      },
      {
        id: 'mask-icon',
        rel: 'icon',
        type: 'image/svg+xml',
        href: '/favicon.svg'
      },
      {
        id: 'apple-touch-icon',
        rel: 'apple-touch-icon',
        href: iconoApple
      },
      {
        id: 'canonical',
        rel: 'canonical',
        href: canonicalUrl
      },

      { id: 'baseCSS', rel: 'stylesheet', href: '/css/porDefecto.css' }
    ],
    script: scripts,
    noscript: gTagManager
      ? [{
        id: 'gtmNoScript',
        tagPosition: 'bodyClose',
        innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${gTagManager}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
      }]
      : []
  }

  return head
}

